import { useCallback, useEffect, useMemo, useState } from "react";
import { FiEye, FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import companyService from "../../../services/company.service";
import { getStoredAuth } from "../../../utils/storage";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import "../../../styles/users.css";
import "../../../styles/ssdiLinkBanker.css";

function dash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function idOf(value) {
  if (value == null) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function matchesQuery(haystack, query) {
  if (!query) return true;
  return String(haystack || "")
    .toLowerCase()
    .includes(query);
}

function SearchablePicker({
  id,
  label,
  query,
  onQueryChange,
  placeholder,
  options,
  value,
  onChange,
  emptyText,
}) {
  const selected = options.find((item) => item.id === value) || null;
  const q = String(query || "").trim().toLowerCase();
  const visible = q
    ? options.filter((item) => matchesQuery(item.searchText || item.title, q))
    : options;

  return (
    <div className="sla-picker">
      <label htmlFor={id}>{label}</label>
      {selected ? (
        <div className="sla-picker__chosen">
          <div className="sla-picker__chosen-text">
            <strong>{selected.title}</strong>
            {selected.subtitle ? <small>{selected.subtitle}</small> : null}
          </div>
          <button
            type="button"
            className="sla-picker__change"
            onClick={() => {
              onChange("");
              onQueryChange("");
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="sla-picker__search">
            <FiSearch aria-hidden />
            <input
              id={id}
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
          </div>
          <ul className="sla-picker__list" role="listbox" aria-label={label}>
            {visible.length === 0 ? (
              <li className="sla-picker__empty">{emptyText}</li>
            ) : (
              visible.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="sla-picker__option"
                    onClick={() => onChange(item.id)}
                  >
                    <strong>{item.title}</strong>
                    {item.subtitle ? <small>{item.subtitle}</small> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
    </div>
  );
}

async function fetchAllPages(loadPage) {
  const limit = PAGE_LIMIT;
  let page = 1;
  let items = [];
  let total = null;
  while (page <= 40) {
    const result = await loadPage(page, limit);
    const batch = Array.isArray(result.items) ? result.items : [];
    items = items.concat(batch);
    const reported = Number(result.total);
    if (Number.isFinite(reported) && reported >= 0) total = reported;
    if (!batch.length || batch.length < limit) break;
    if (total != null && items.length >= total) break;
    page += 1;
  }
  return items;
}

function companiesFromResponse(res) {
  return (
    res?.data?.items ||
    res?.data?.companies ||
    res?.items ||
    res?.companies ||
    []
  );
}

export default function SsdiLinkBankerAgency() {
  const [banks, setBanks] = useState([]);
  const [links, setLinks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [viewBank, setViewBank] = useState(null);
  const [agencySearch, setAgencySearch] = useState("");
  const [removingId, setRemovingId] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [bankerQuery, setBankerQuery] = useState("");
  const [agencyQuery, setAgencyQuery] = useState("");
  const [selectedBankerId, setSelectedBankerId] = useState("");
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState("");

  const loadAll = useCallback(async () => {
    const token = getStoredAuth()?.token || "";
    const [bankRows, linkRows, companyRows] = await Promise.all([
      fetchAllPages(async (pageNum, limit) => {
        const res = await bankService.ssdiListBanks({ page: pageNum, limit });
        return {
          items: res?.data?.data?.banks || [],
          total: res?.data?.data?.total ?? 0,
        };
      }),
      fetchAllPages(async (pageNum, limit) => {
        const res = await bankService.ssdiListLinks({ page: pageNum, limit });
        return {
          items: res?.data?.data?.links || [],
          total: res?.data?.data?.total ?? 0,
        };
      }),
      fetchAllPages(async (pageNum, limit) => {
        const res = await companyService.getCompanies(token, {
          page: pageNum,
          limit,
        });
        return {
          items: companiesFromResponse(res),
          total: res?.data?.total ?? res?.pagination?.total ?? 0,
        };
      }),
    ]);
    setBanks(bankRows);
    setLinks(linkRows.filter((row) => row?.isActive !== false));
    setCompanies(companyRows);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    loadAll()
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Failed to load bankers.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  useEffect(() => {
    if (!createOpen && !viewBank) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        setCreateOpen(false);
        setViewBank(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createOpen, viewBank]);

  const companyByAdminId = useMemo(() => {
    const map = new Map();
    for (const company of companies) {
      const adminId = idOf(company.repoAdminUserId);
      if (adminId && !map.has(adminId)) map.set(adminId, company);
    }
    return map;
  }, [companies]);

  const linksByBankId = useMemo(() => {
    const map = new Map();
    for (const link of links) {
      const bankId = idOf(link.bankId);
      if (!bankId) continue;
      const list = map.get(bankId) || [];
      list.push(link);
      map.set(bankId, list);
    }
    return map;
  }, [links]);

  const bankerRows = useMemo(
    () =>
      banks.map((bank) => {
        const bankId = idOf(bank._id);
        const bankLinks = linksByBankId.get(bankId) || [];
        return {
          bank,
          bankId,
          adminId: idOf(bank.adminUserId),
          linkCount: bankLinks.length,
        };
      }),
    [banks, linksByBankId]
  );

  const filteredBankers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bankerRows;
    return bankerRows.filter(({ bank }) =>
      [bank.bankName, bank.bankCode, bank.email, bank.branchName, bank.phone].some(
        (field) => matchesQuery(field, q)
      )
    );
  }, [bankerRows, search]);

  const totalPages = Math.ceil(filteredBankers.length / PAGE_LIMIT) || 0;
  const safePage = Math.min(page, Math.max(totalPages, 1));
  const pagedBankers = filteredBankers.slice(
    (safePage - 1) * PAGE_LIMIT,
    safePage * PAGE_LIMIT
  );

  const totalLinkedAgencies = links.length;

  const openView = (bank) => {
    setNotice("");
    setAgencySearch("");
    setViewBank(bank);
  };

  const viewLinks = useMemo(() => {
    if (!viewBank) return [];
    return linksByBankId.get(idOf(viewBank._id)) || [];
  }, [viewBank, linksByBankId]);

  const filteredViewLinks = useMemo(() => {
    const q = agencySearch.trim().toLowerCase();
    return viewLinks.filter((link) => {
      const admin = link.repoAdminId || {};
      const company = companyByAdminId.get(idOf(admin));
      return [
        company?.companyName,
        company?.companyCode,
        admin.name,
        admin.email,
        admin.phone,
        company?.email,
        company?.phone,
        company?.address,
        company?.district,
        admin.district,
        company?.state,
        admin.state,
        company?.pincode,
        admin.pincode,
      ].some((field) => matchesQuery(field, q));
    });
  }, [viewLinks, agencySearch, companyByAdminId]);

  const agencyOptions = useMemo(
    () =>
      companies
        .map((company) => {
          const admin = company.repoAdminUserId;
          const adminId = idOf(admin);
          if (!adminId) return null;
          const email = admin?.email || company.email || "";
          return {
            id: adminId,
            title: company.companyName || "Agency",
            subtitle: [company.companyCode, admin?.name, email].filter(Boolean).join(" · "),
            searchText: [company.companyName, company.companyCode, admin?.name, email].join(" "),
          };
        })
        .filter(Boolean),
    [companies]
  );

  const bankerOptions = useMemo(
    () =>
      bankerRows
        .filter((row) => row.adminId)
        .map(({ bank, adminId }) => ({
          id: adminId,
          title: bank.bankName || "Bank",
          subtitle: [bank.bankCode, bank.email].filter(Boolean).join(" · "),
          searchText: [bank.bankName, bank.bankCode, bank.email].join(" "),
        })),
    [bankerRows]
  );

  const refreshQuiet = async () => {
    await loadAll();
  };

  const handleCreateLink = async (event) => {
    event.preventDefault();
    setLinkError("");
    setNotice("");
    if (!selectedBankerId) {
      setLinkError("Select a banker.");
      return;
    }
    if (!selectedAgencyId) {
      setLinkError("Select an agency.");
      return;
    }
    setLinkSaving(true);
    try {
      await bankService.ssdiCreateLink({
        bankPersonId: selectedBankerId,
        repoAdminId: selectedAgencyId,
      });
      setCreateOpen(false);
      setSelectedBankerId("");
      setSelectedAgencyId("");
      setBankerQuery("");
      setAgencyQuery("");
      await refreshQuiet();
      setNotice("Banker–agency link created.");
    } catch (err) {
      setLinkError(err?.response?.data?.message || "Failed to create link.");
    } finally {
      setLinkSaving(false);
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (!window.confirm("Remove this banker–agency link?")) return;
    setRemovingId(linkId);
    setError("");
    try {
      await bankService.ssdiDeleteLink(linkId);
      await refreshQuiet();
      setNotice("Link removed.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to remove link.");
    } finally {
      setRemovingId("");
    }
  };

  return (
    <section className="create-company-card ssdi-bank-details-card sla-card">
      <header className="sla-head">
        <div>
          <h3>Link Banker → Agency (Repo Admin)</h3>
          <p className="sla-stats">
            {filteredBankers.length} banker{filteredBankers.length === 1 ? "" : "s"}
            <span aria-hidden> · </span>
            {totalLinkedAgencies} linked agenc
            {totalLinkedAgencies === 1 ? "y" : "ies"}
          </p>
        </div>
        <button
          type="button"
          className="primary-page-btn"
          onClick={() => {
            setLinkError("");
            setBankerQuery("");
            setAgencyQuery("");
            setSelectedBankerId("");
            setSelectedAgencyId("");
            setCreateOpen(true);
          }}
        >
          <FiPlus aria-hidden />
          Create Link
        </button>
      </header>

      <div className="company-search-panel company-search-panel--simple sla-search">
        <div className="company-search-bar">
          <FiSearch className="company-search-icon" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by bank name, code, email, branch or phone…"
            aria-label="Search bankers"
          />
        </div>
      </div>

      {notice ? <p className="sla-notice">{notice}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {loading ? (
        <p className="muted">Loading bankers…</p>
      ) : (
        <>
          <div className="company-table-wrap ssdi-excel-sheet table-scroll">
            <table className="users-table excel-grid-table">
              <thead>
                <tr>
                  <th>Bank Name</th>
                  <th>Code</th>
                  <th>Email</th>
                  <th>Branch</th>
                  <th>Phone</th>
                  <th>Linked Agencies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedBankers.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      {search.trim()
                        ? "No bankers match your search."
                        : "No bankers found."}
                    </td>
                  </tr>
                ) : (
                  pagedBankers.map(({ bank, bankId, linkCount }) => (
                    <tr key={bankId}>
                      <td>{dash(bank.bankName)}</td>
                      <td>{dash(bank.bankCode)}</td>
                      <td>{dash(bank.email)}</td>
                      <td>{dash(bank.branchName)}</td>
                      <td>{dash(bank.phone)}</td>
                      <td>
                        {linkCount} {linkCount === 1 ? "Agency" : "Agencies"}
                      </td>
                      <td className="sla-actions-td">
                        <button
                          type="button"
                          className="company-btn company-btn--view sla-view-btn"
                          onClick={() => openView(bank)}
                          aria-label={`View ${bank.bankName || "banker"}`}
                        >
                          <FiEye aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={safePage}
            totalPages={totalPages}
            total={filteredBankers.length}
            loading={loading}
            onPageChange={setPage}
          />
        </>
      )}

      {viewBank ? (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => setViewBank(null)}
        >
          <div
            className="modal-box sla-modal sla-modal--view"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sla-view-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sla-modal__head">
              <h3 id="sla-view-title">{dash(viewBank.bankName)}</h3>
              <button
                type="button"
                className="sla-close"
                onClick={() => setViewBank(null)}
                aria-label="Close banker details"
              >
                <FiX aria-hidden />
              </button>
            </div>

            <h4 className="sla-subhead">Linked Agencies</h4>
            <div className="company-search-bar sla-modal-search">
              <FiSearch className="company-search-icon" aria-hidden />
              <input
                type="search"
                value={agencySearch}
                onChange={(e) => setAgencySearch(e.target.value)}
                placeholder="Search by name, code, email, phone, address, or pincode…"
                aria-label="Search linked agencies"
              />
            </div>
            <div className="company-table-wrap ssdi-excel-sheet table-scroll">
              <table className="users-table excel-grid-table">
                <thead>
                  <tr>
                    <th>Agency Name</th>
                    <th>Agency Code</th>
                    <th>Admin Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>District</th>
                    <th>State</th>
                    <th>Pincode</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredViewLinks.length === 0 ? (
                    <tr>
                      <td colSpan={10}>
                        {viewLinks.length === 0
                          ? "No agencies linked to this banker."
                          : "No linked agencies match your search."}
                      </td>
                    </tr>
                  ) : (
                    filteredViewLinks.map((link) => {
                      const admin = link.repoAdminId || {};
                      const company = companyByAdminId.get(idOf(admin));
                      return (
                        <tr key={idOf(link._id)}>
                          <td>{dash(company?.companyName || admin.name)}</td>
                          <td>{dash(company?.companyCode)}</td>
                          <td>{dash(admin.name)}</td>
                          <td>{dash(admin.email || company?.email)}</td>
                          <td>{dash(admin.phone || company?.phone)}</td>
                          <td>{dash(company?.address)}</td>
                          <td>{dash(company?.district || admin.district)}</td>
                          <td>{dash(company?.state || admin.state)}</td>
                          <td>{dash(company?.pincode || admin.pincode)}</td>
                          <td className="sla-actions-td">
                            <button
                              type="button"
                              className="company-btn company-btn--delete"
                              disabled={removingId === idOf(link._id)}
                              onClick={() => handleRemoveLink(idOf(link._id))}
                            >
                              <FiTrash2 aria-hidden />
                              <span>
                                {removingId === idOf(link._id) ? "Removing…" : "Remove"}
                              </span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => !linkSaving && setCreateOpen(false)}
        >
          <div
            className="modal-box sla-modal sla-modal--create"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sla-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sla-modal__head">
              <div>
                <h3 id="sla-create-title">Create Link</h3>
                <p className="sla-modal__hint">Connect one bank with one repo agency.</p>
              </div>
              <button
                type="button"
                className="sla-close"
                onClick={() => !linkSaving && setCreateOpen(false)}
                aria-label="Close create link"
              >
                <FiX aria-hidden />
              </button>
            </div>
            <form className="sla-create-form" onSubmit={handleCreateLink}>
              <SearchablePicker
                id="sla-banker-search"
                label="Banker"
                query={bankerQuery}
                onQueryChange={setBankerQuery}
                placeholder="Search bank name, code, or email"
                options={bankerOptions}
                value={selectedBankerId}
                onChange={setSelectedBankerId}
                emptyText="No bankers match your search."
              />
              <SearchablePicker
                id="sla-agency-search"
                label="Agency / Repo Admin"
                query={agencyQuery}
                onQueryChange={setAgencyQuery}
                placeholder="Search agency name, code, admin, or email"
                options={agencyOptions}
                value={selectedAgencyId}
                onChange={setSelectedAgencyId}
                emptyText="No agencies match your search."
              />
              {linkError ? <p className="error-text">{linkError}</p> : null}
              <div className="sla-create-actions">
                <button
                  type="button"
                  className="secondary-page-btn"
                  onClick={() => setCreateOpen(false)}
                  disabled={linkSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-page-btn" disabled={linkSaving}>
                  {linkSaving ? "Creating…" : "Create Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
